/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useState } from 'react'
import { useToast } from '@chakra-ui/react'

import { AddPlayerFormData } from '@common/types'
import { getPlayerDetailUrl } from '@common/utils'

import { baseStatsApiURL, superscoreBaseWebURL } from '../common/config'
import { useUser } from '../queries/useUser'

async function fetchData(playerId?: string, accessToken?: string) {
    if (!playerId) {
        throw new Error('No player id provided')
    }

    if (!accessToken) {
        throw new Error('No access token provided')
    }

    const url = new URL(`${baseStatsApiURL}/v2/soccer/players/overview/comsuperbetsport/en-GB`)
    url.searchParams.set('player-id', playerId)

    const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    })

    if (response.status === 204) {
        throw new Error('Player not found')
    }

    if (!response.ok) {
        throw new Error('Failed to fetch player')
    }

    const json = await response.json()

    return json
}

function transformPlayer(playerOverview: any): AddPlayerFormData | null {
    if (!playerOverview || !playerOverview.player) {
        return null
    }

    const { id, name, seo_id } = playerOverview.player
    const shirtNumber = Number(playerOverview.team_infos?.[0]?.shirt_number?.value)

    const playerDetailUrl =
        seo_id?.hash?.value && seo_id?.slug?.value
            ? getPlayerDetailUrl(superscoreBaseWebURL, seo_id.slug?.value, seo_id.hash?.value)
            : null

    return {
        id,
        surname: name,
        shirt_number: !Number.isNaN(shirtNumber) && shirtNumber > 0 ? shirtNumber : undefined,
        captain: false,
        playerDetailUrl,
    }
}

export function useGetPlayerById() {
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const { userData } = useUser()

    const toast = useToast()

    const getPlayerById = useCallback(
        async (id: string): Promise<AddPlayerFormData | null> => {
            if (!id || id.includes('fake')) {
                return null
            }

            setIsError(false)

            setIsLoading(true)

            try {
                const response = await fetchData(id, userData?.accessToken)

                return transformPlayer(response)
            } catch (error) {
                setIsError(true)

                toast({
                    position: 'top',
                    title: error instanceof Error ? error.message : 'Error fetching player!',
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })

                return null
            } finally {
                setIsLoading(false)
            }
        },
        [userData?.accessToken, toast],
    )

    return { getPlayerById, isLoading, isError }
}
